import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getRequestVendorPreIsuProducts,
  saveVendorPreIsu,
  exportToExcel,
} from '../../services/internal/request-vendor-pre-isu'

import {
  PreISUProductsResponse,
  mergePreISUProductsResponses,
} from '../../models/products/preISUProductsResponse'
import { Product } from 'models/products/product'

export const fetchRequestVendorPreIsuProductsAction = createAsyncThunk(
  'requestVendorPreIsu/fetchRequestVendorPreIsuProducts',
  async (data: { eventId; request }) => {
    const { eventId, request } = data

    const page = 1
    const perPage = 1000 // max products to fetch at a time

    const params = {
      eventId,
      page,
      perPage,
      request,
    }

    const res = await getRequestVendorPreIsuProducts(params)

    // Since we can only get 1000 top-level products at a time (even if the number of products returned is much larger)
    // we have to get the initial response, with the count, then request the rest, merging the responses
    const promises: Promise<PreISUProductsResponse>[] = []
    const count = res?.products?.totalItemsIncludingLevels ?? 0
    for (
      let requestPage = 1;
      requestPage * res.products.per_page < count;
      requestPage++
    ) {
      promises.push(
        getRequestVendorPreIsuProducts({
          ...params,
          page: requestPage + 1,
        }),
      )
    }

    const results = await Promise.all(promises)

    const finalResponse = results.reduce((accumulatorResponse, response) => {
      return mergePreISUProductsResponses(accumulatorResponse, response)
    }, res)

    return finalResponse
  },
)

export const saveVendorPreIsuAction = createAsyncThunk(
  'requestVendorPreIsu/saveVendorPreIsu',
  async (data: { eventId; request }) => {
    const { eventId, request } = data
    return await saveVendorPreIsu(eventId, request)
  },
)

export const exportToExcelAction = createAsyncThunk(
  'requestVendorPreIsu/exportToExcel',
  async (data: { fileName; eventId; totalCount; request }) => {
    const { fileName, eventId, totalCount, request } = data
    return await exportToExcel({ fileName, eventId, totalCount, request })
  },
)

const initialState = {
  products: [] as Product[],
  bids: [],
  isFetchingRequestVendorPreIsuProducts: false,
  vendorPreIsuCountByBpIdMap: {},
  productsTotalCount: 0,
  isDownloading: false,
}

const requestVendorPreIsuSlice = createSlice({
  name: 'requestVendorPreIsu',
  initialState,
  reducers: {
    resetRequestVendorPreIsu: state => {
      state.products = []
      state.bids = []
      state.isFetchingRequestVendorPreIsuProducts = false
      state.vendorPreIsuCountByBpIdMap = {}
      state.productsTotalCount = 0
      state.isDownloading = false
    },
  },
  extraReducers: builder => {
    builder

      // fetchRequestVendorPreIsuProductsActions
      .addCase(fetchRequestVendorPreIsuProductsAction.pending, state => {
        state.isFetchingRequestVendorPreIsuProducts = true
      })
      .addCase(
        fetchRequestVendorPreIsuProductsAction.fulfilled,
        (state, action) => {
          state.products = action.payload.products.values
          state.bids = action.payload.vendors.bids
          state.productsTotalCount =
            action.payload.products.totalItemsIncludingLevels
          state.isFetchingRequestVendorPreIsuProducts = false
        },
      )
      .addCase(fetchRequestVendorPreIsuProductsAction.rejected, state => {
        state.isFetchingRequestVendorPreIsuProducts = false
      })

      // exportToExcelAction
      .addCase(exportToExcelAction.pending, state => {
        state.isDownloading = true
      })
      .addCase(exportToExcelAction.fulfilled, state => {
        state.isDownloading = false
      })
      .addCase(exportToExcelAction.rejected, state => {
        state.isDownloading = false
      })
  },
})

export const { resetRequestVendorPreIsu } = requestVendorPreIsuSlice.actions

export default requestVendorPreIsuSlice.reducer
